import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAppointmentsByStatus } from "@/lib/queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate } from "@emr/shared/india";
import { useState } from "react";
import { ConfirmAppointmentDialog } from "./ConfirmAppointmentDialog";
import { RebookDialog } from "./RebookDialog";
import { BulkRebookDialog } from "./BulkRebookDialog";
import { CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";
import type { AppointmentRow } from "@/lib/queries";

/**
 * Pending confirmations panel.
 *
 * - When `date` is provided, shows only pending appointments whose
 *   arrivalTime falls on that date (slot-bound pending) — used by the
 *   Pending Appointments page alongside the calendar.
 * - When `date` is omitted, shows ALL pending appointments regardless of
 *   date (slot-less + slot-bound) — the original behavior.
 * - ADMINS get bulk selection (2026-10-31): per-row checkboxes + select-all,
 *   and bulk "Cancel selected" / "Reschedule selected" actions. Both loop
 *   the existing per-row endpoints (`PATCH /appointments/:id`,
 *   `POST /appointments/:id/rebook`) so every server-side guard still runs
 *   per appointment; per-row failures are collected, never fatal to the
 *   batch. Non-admins see no selection UI (checkbox column and bulk bar are
 *   admin-gated; the per-row Cancel/Reschedule buttons stay
 *   permission-gated as before).
 */
export function PendingConfirmationsPanel({ date }: { date?: string }) {
  // Always fetch the full pending set (slot-less pending have no date, so a
  // date-filtered query would miss them). We filter client-side when a date
  // is supplied so the query cache key stays stable.
  const { data, isLoading } = useAppointmentsByStatus("Pending");
  const [confirmAppt, setConfirmAppt] = useState<AppointmentRow | null>(null);
  const [rebookAppt, setRebookAppt] = useState<AppointmentRow | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const qc = useQueryClient();
  const canEdit = isAdmin || permissions.has("appointments.edit");
  const allRows = data?.rows ?? [];
  const rows = date
    ? allRows.filter((a) => (a.arrivalTime ?? "").slice(0, 10) === date)
    : allRows;

  // ---- Admin bulk selection (2026-10-31) ----
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRebookOpen, setBulkRebookOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const selectedRows = rows.filter((a) => selectedIds.has(a.id));
  const allSelected = isAdmin && rows.length > 0 && selectedRows.length === rows.length;

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(rows.map((a) => a.id)));
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["appointments"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const cancelPending = async (appt: AppointmentRow) => {
    if (!window.confirm(`Cancel ${appt.patientName ?? "this patient"}'s pending appointment?`)) return;
    setCancellingId(appt.id);
    try {
      await apiFetch(`/appointments/${appt.id}`, { method: "PATCH", body: JSON.stringify({ status: "Cancelled" }) });
      invalidate();
      toast.success("Pending appointment cancelled");
    } catch (e) {
      const err = e as Error & { body?: { error?: string; message?: string } };
      toast.error(err?.body?.message || err?.body?.error || "Failed to cancel");
    } finally {
      setCancellingId(null);
    }
  };

  // Bulk cancel — sequential PATCHes so each row's guards run per row;
  // per-row failures are collected and reported, never abort the batch.
  const bulkCancel = async () => {
    if (!selectedRows.length || bulkBusy) return;
    if (!window.confirm(`Cancel ${selectedRows.length} selected pending appointment${selectedRows.length > 1 ? "s" : ""}?`)) return;
    setBulkBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const r of selectedRows) {
      try {
        await apiFetch(`/appointments/${r.id}`, { method: "PATCH", body: JSON.stringify({ status: "Cancelled" }) });
        ok++;
      } catch {
        failed.push(`${r.patientName ?? r.id} (${r.clinicUhid ?? "—"})`);
      }
    }
    setBulkBusy(false);
    if (ok > 0) {
      invalidate();
      toast.success(`Cancelled ${ok}/${selectedRows.length}`);
    }
    if (failed.length) toast.error(`Failed: ${failed.join(", ")}`);
    setSelectedIds(new Set());
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-maroon-700 dark:text-amber-500">
            Pending confirmations ({rows.length})
          </CardTitle>
          {/* Admin bulk bar (2026-10-31): select + bulk cancel/reschedule. */}
          {isAdmin && rows.length > 0 && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={toggleAll} className="text-muted">
                {allSelected ? "Clear selection" : "Select all"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={bulkCancel}
                disabled={selectedRows.length === 0 || bulkBusy}
                className="text-red-600 hover:text-red-700"
              >
                {bulkBusy ? "Cancelling…" : `Cancel selected (${selectedRows.length})`}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setBulkRebookOpen(true)}
                disabled={selectedRows.length === 0 || bulkBusy}
                className="text-maroon-600 hover:text-maroon-700"
              >
                <CalendarClock size={14} className="mr-1" /> Reschedule selected ({selectedRows.length})
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? <Skeleton className="h-20" /> : rows.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted">No pending appointments{date ? ` for ${fmtDate(date)}` : ""}.</div>
        ) : (
          <ul className="divide-y divide-cream-100 dark:divide-dark-700">
            {rows.map((a) => (
              <li key={a.id} className="flex items-center justify-between px-4 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  {/* Admin-only selection checkbox (2026-10-31). */}
                  {isAdmin && (
                    <Checkbox
                      aria-label={`Select ${a.patientName ?? "appointment"}`}
                      checked={selectedIds.has(a.id)}
                      onCheckedChange={(v) => toggleOne(a.id, v === true)}
                    />
                  )}
                  <button className="min-w-0 text-left" onClick={() => a.patientId && navigate(`/patients/${a.patientId}`)}>
                    <div className="truncate text-sm font-medium">{a.patientName ?? "—"}</div>
                    <div className="truncate text-xs text-muted">{a.clinicUhid} · created {fmtDate(a.createdAt)}</div>
                  </button>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => setRebookAppt(a)} disabled={cancellingId === a.id} title="Reschedule" className="text-maroon-600 hover:text-maroon-700">
                      <CalendarClock size={14} className="mr-1" /> Reschedule
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => cancelPending(a)} disabled={cancellingId === a.id} title="Cancel pending" className="text-red-600 hover:text-red-700">
                      Cancel
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setConfirmAppt(a)}>Confirm</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <ConfirmAppointmentDialog open={!!confirmAppt} onOpenChange={(v) => !v && setConfirmAppt(null)} appointment={confirmAppt} />
      <RebookDialog open={!!rebookAppt} onOpenChange={(v) => !v && setRebookAppt(null)} appointment={rebookAppt} />
      <BulkRebookDialog
        open={bulkRebookOpen}
        onOpenChange={setBulkRebookOpen}
        rows={selectedRows}
        onDone={() => setSelectedIds(new Set())}
      />
    </Card>
  );
}